import React from 'react';
import { useAuth } from '../../services/useAuth';
import { SiteLink, useRouter } from '../../../../components';
import env from '../../../../env';

export function Signup(props) {
  const [email, setEmail] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [confirmPassword, setConfirmPassword] = React.useState("");
  const [firstName, setFirstName] = React.useState("");
  const [lastName, setLastName] = React.useState("");
  const [error, setError] = React.useState("");
  const [pending, setPending] = React.useState(false);
  const [agreed, setAgreed] = React.useState(false);
  const [showPassword, setShowPassword] = React.useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = React.useState(false);
  
  const auth = useAuth();
  const router = useRouter();

  const passwordsMatch = password && confirmPassword && password === confirmPassword;

  async function submit(e) {
    e.preventDefault();
    e.stopPropagation();
    
    // Validation
    if (!email || !password || !confirmPassword) {
      setError('Please fill in all required fields.');
      return;
    }
    
    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    
    if (password.length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }
    
    if (!agreed) {
      setError('Please agree to the Privacy Policy.');
      return;
    }

    setPending(true);
    setError('');

    try {
      // 1. Create Firebase user
      const firebaseUser = await auth.signup(email, password);
      
      // 2. Create user profile in database and send welcome email
      const response = await fetch(env.apiBase + '/api/profile/UpdateUserProfile', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          uid: firebaseUser.uid,
          email: firebaseUser.email,
          firstName: firstName,
          lastName: lastName,
          emailVerified: firebaseUser.emailVerified,
          verifyEmail: false,
          sendWelcomeEmail: true
        })
      });

      if (!response.ok) {
        throw new Error('Failed to create user profile');
      }

      setPending(false);
      setError('');
      
      // Redirect to members area after successful registration
      router.push('/members-area');
      
    } catch (error) {
      console.error('Signup error:', error);
      setPending(false);
      
      // Handle Firebase errors
      if (error.code === 'auth/email-already-in-use') {
        setError('This email is already registered. Please sign in instead.');
      } else if (error.code === 'auth/invalid-email') {
        setError('Invalid email address.');
      } else if (error.code === 'auth/weak-password') {
        setError('Password is too weak. Please use at least 6 characters.');
      } else {
        setError(error.message || 'Registration failed. Please try again.');
      }
    }
  }

  return (
    <div className="sign-up auth-panel" style={{maxWidth: '600px', margin: 'auto'}}>
      <h1>Sign Up</h1>
      <div className="loading-panel-holder">
        <div className="panel-content">
          <form method="POST" noValidate="" onSubmit={submit}>
            <div className="form-group">
              <label htmlFor="firstName">First Name *</label>
              <input 
                id="firstName" 
                type="text" 
                className="form-control" 
                name="firstName"
                required
                value={firstName}
                onChange={event => setFirstName(event.target.value)} 
              />
            </div>
            <div className="form-group">
              <label htmlFor="lastName">Last Name *</label>
              <input 
                id="lastName" 
                type="text" 
                className="form-control" 
                name="lastName"
                required
                value={lastName}
                onChange={event => setLastName(event.target.value)}
              />
            </div>
            <div className="form-group">
              <label htmlFor="email">Email *</label>
              <input 
                id="email" 
                type="email" 
                className="form-control" 
                name="email" 
                required 
                autoFocus
                value={email}
                onChange={event => setEmail(event.target.value)} 
              />
              <div className="invalid-feedback">
                Email is invalid
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="password">Password *</label>
              <div style={{ position: 'relative' }}>
                <input 
                  id="password" 
                  type={showPassword ? 'text' : 'password'} 
                  className="form-control" 
                  name="password" 
                  required
                  value={password}
                  onChange={event => setPassword(event.target.value)} 
                />
                <span
                  onClick={() => setShowPassword(!showPassword)}
                  style={{
                    position: 'absolute',
                    right: '10px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    cursor: 'pointer',
                    color: '#007bff',
                    fontSize: '0.85rem'
                  }}
                >
                  {showPassword ? 'hide' : 'show'}
                </span>
              </div>
              <small className="form-text text-muted">Password must be at least 6 characters</small>
            </div>

            <div className="form-group">
              <label htmlFor="confirmPassword">Confirm Password *</label>
              <div style={{ position: 'relative' }}>
                <input 
                  id="confirmPassword" 
                  type={showConfirmPassword ? 'text' : 'password'} 
                  className="form-control" 
                  name="confirmPassword" 
                  required
                  value={confirmPassword}
                  onChange={event => setConfirmPassword(event.target.value)} 
                />
                {passwordsMatch && (
                  <span style={{
                    position: 'absolute',
                    right: '50px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    color: '#28a745',
                    fontWeight: 'bold',
                    fontSize: '0.85rem'
                  }}>
                    ✓
                  </span>
                )}
                <span
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  style={{
                    position: 'absolute',
                    right: '10px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    cursor: 'pointer',
                    color: '#007bff',
                    fontSize: '0.85rem'
                  }}
                >
                  {showConfirmPassword ? 'hide' : 'show'}
                </span>
              </div>
            </div>

            <div className="form-group">
              <div className='form-check'>
                <input 
                  type="checkbox" 
                  name="privacy" 
                  id="privacy" 
                  className="form-check-input" 
                  checked={agreed} 
                  onChange={(e) => setAgreed(e.target.checked)} 
                  required 
                />
                <label htmlFor="privacy" className="custom-control-label">
                  I agree to the <SiteLink to="/privacy">Privacy and Confidentiality Policy</SiteLink>
                </label>
              </div>
            </div>

            <div className="form-group m-0">
              <button type="submit" disabled={pending} className="btn btn-primary btn-block">
                {pending && <span className="spinner-grow spinner-grow-sm" role="status" aria-hidden="true"></span>}
                <span className="">Sign Up</span>
              </button>
            </div>

            {error && <div style={{color: 'red', marginTop: '10px'}}>{error}</div>}

            <div className="text-center mt-3">
              Already have an account?{' '}
              <SiteLink to="/login" style={{color: '#e21d20', fontWeight: 'bold', textDecoration: 'underline'}}>
                Log in
              </SiteLink>
            </div>

          </form>
        </div>
      </div>
    </div>
  );
}

